/**
 * QUIPWA — the themes that ship with the app
 *
 * Presets are read-only. Changing anything on one starts a draft copy that
 * the user names and saves explicitly (theme-store: `editActive`, `saveDraft`).
 *
 * The four cover the real use scenes rather than taste variations:
 *   Quipwa Dark     the identity; long sessions in a dim room
 *   Papel           daylight reading, warm off-white like a notebook page
 *   Alto contraste  low vision or bright glare; larger type, pure black/white
 *   Foro retro      the playful one (kept from the first version)
 */
import type { ThemeProfile } from './theme-store'

export const FRAUNCES = '"Fraunces", Georgia, serif'
export const JETBRAINS = '"JetBrains Mono", "Fira Code", monospace'

export const DEFAULT_PROFILE_ID = 'default'

export const DEFAULT_PROFILES: readonly ThemeProfile[] = [
  {
    id: DEFAULT_PROFILE_ID,
    name: 'Quipwa Dark',
    is_custom: false,
    updated_at: 0,
    colors: { primary: '#1D9E75', bgBase: '#0A0F0B', surface: '#111710', textTitle: '#E8E0D0', textBody: '#9E9588' },
    background: { type: 'solid', value: '#0A0F0B', patternStyle: 'none', opacity: 1, blur: 0 },
    typography: { titleFont: FRAUNCES, bodyFont: JETBRAINS, cardFont: JETBRAINS, baseSize: 13, cardFontSize: 20 },
    effects: { cardRadius: 12, cardShadow: '0 4px 20px rgba(0,0,0,0.5)', globalOpacity: 1, flipAnimation: 'flip-h' },
  },
  {
    id: 'paper',
    name: 'Papel',
    is_custom: false,
    updated_at: 0,
    colors: { primary: '#17785A', bgBase: '#F3EEE3', surface: '#FBF8F1', textTitle: '#1E2A22', textBody: '#465049' },
    background: { type: 'solid', value: '#F3EEE3', patternStyle: 'none', opacity: 1, blur: 0 },
    typography: { titleFont: FRAUNCES, bodyFont: JETBRAINS, cardFont: FRAUNCES, baseSize: 14, cardFontSize: 22 },
    effects: { cardRadius: 14, cardShadow: '0 6px 24px rgba(30,42,34,0.12)', globalOpacity: 1, flipAnimation: 'flip-h' },
  },
  {
    id: 'high-contrast',
    name: 'Alto contraste',
    is_custom: false,
    updated_at: 0,
    colors: { primary: '#FFD400', bgBase: '#000000', surface: '#0F0F0F', textTitle: '#FFFFFF', textBody: '#F0F0F0' },
    background: { type: 'solid', value: '#000000', patternStyle: 'none', opacity: 1, blur: 0 },
    typography: { titleFont: FRAUNCES, bodyFont: JETBRAINS, cardFont: JETBRAINS, baseSize: 16, cardFontSize: 24 },
    effects: { cardRadius: 8, cardShadow: '0 4px 16px rgba(0,0,0,0.8)', globalOpacity: 1, flipAnimation: 'fade' },
  },
  {
    id: 'retro-forum',
    name: 'Foro retro',
    is_custom: false,
    updated_at: 0,
    colors: { primary: '#FF00FF', bgBase: '#000080', surface: '#1A1A8C', textTitle: '#FFFF00', textBody: '#FFFFFF' },
    background: { type: 'pattern', value: '#000080', patternStyle: 'splatter', opacity: 1, blur: 0 },
    typography: { titleFont: '"Comic Sans MS", cursive', bodyFont: '"Courier New", monospace', cardFont: '"Courier New", monospace', baseSize: 14, cardFontSize: 20 },
    // The block shadow is this theme's whole joke: a 2004 forum.
    effects: { cardRadius: 0, cardShadow: '4px 4px 0px #000000', globalOpacity: 1, flipAnimation: 'flip-h' },
  },
]

export function findPreset(id: string | undefined): ThemeProfile | undefined {
  return DEFAULT_PROFILES.find((p) => p.id === id)
}
